import { PERMISSIONS, type PermissionKey } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useInfiniteQuery, useMutation } from "@tanstack/react-query"

import { apiClientV2 } from "../../../services/api/apiClient"
import { mcCursorList } from "./mcQueryHelpers"
import type { MemoryLineageEvent } from "./types"

/**
 * Turnus Memory — the feedback half.
 *
 * NONE OF THESE WRITE COMPLIANCE DATA, and that is the point. Accepting a suggested
 * supplier does not bind the declaration: the screen calls the ordinary assign endpoint
 * for that, exactly as it does for a supplier picked by hand, and then tells memory what
 * happened. So the compliance write stays owned, validated and audited by the code that
 * owns it, and the correction is indistinguishable from a manual one.
 *
 * ORDER MATTERS at the call site: report the accept only AFTER the product write has
 * succeeded. Reporting first would raise the entry's confidence on the strength of
 * something that then failed.
 *
 * All three are FIRE AND FORGET from the screen's point of view. A suggestion that was
 * taken is already taken; a failure to record that is a lost measurement, not a lost
 * edit, and it must never surface as an error over a supplier assignment that worked.
 */

export interface MemoryFeedbackTarget {
  entryId: string
  surface: string
  targetType?: string
  targetId?: string
}

/**
 * The right a person needs before they may act on a suggestion, which is whatever the
 * underlying edit needs — NOT one fixed permission.
 *
 * WHY THIS IS A PARAMETER. Memory started on declaration review and both hooks hardcoded
 * that permission. A questionnaire suggestion is not a declaration review, and most people
 * who answer questionnaires do not hold `mc.declarations.review` — the guard would have
 * short-circuited every accept, revert and sighting on the new surface, silently, because
 * `useGuardedMutation` fails quiet by design. The backend makes the same choice per surface.
 */
export type MemoryActionPermission = PermissionKey

interface MemoryFeedbackResponse {
  entryId: string
  recorded: boolean
  counted: boolean
  confidence?: number | null
  status?: string | null
  demotedTo?: string | null
  /** Only set by resolve-conflict: the other side(s), now retired. */
  retiredEntryIds?: string[]
}

async function postFeedback(
  action: "accept" | "revert" | "resolve-conflict",
  { entryId, surface, targetType, targetId }: MemoryFeedbackTarget,
): Promise<MemoryFeedbackResponse> {
  const { data } = await apiClientV2.post(
    `/material-compliance/memory/${entryId}/${action}`,
    { surface, targetType, targetId },
  )
  return data
}

/** A person took the suggestion. Call after the product write succeeded. */
export function useAcceptMemory(
  permission: MemoryActionPermission = PERMISSIONS.MC_DECLARATIONS_REVIEW,
) {
  const mutation = useMutation({
    mutationFn: (target: MemoryFeedbackTarget) =>
      postFeedback("accept", target),
  })
  return useGuardedMutation(mutation, permission)
}

/**
 * A person rejected the suggestion.
 *
 * Three of these retire the entry, so this is the only route by which a wrong memory
 * stops appearing. It is deliberately as easy to reach as accept: a feature that is quick
 * to trust and slow to correct is worse than one that does neither.
 */
export function useRevertMemory(
  permission: MemoryActionPermission = PERMISSIONS.MC_DECLARATIONS_REVIEW,
) {
  const mutation = useMutation({
    mutationFn: (target: MemoryFeedbackTarget) =>
      postFeedback("revert", target),
  })
  return useGuardedMutation(mutation, permission)
}

/**
 * A person settled a contradiction (#6b): `target.entryId` is the side they confirmed
 * correct. The response's `retiredEntryIds` names every other side, now gone — the caller
 * removes them from the conflict list itself rather than waiting on a refetch.
 *
 * Same permission reasoning as accept/revert: resolving a conflict IS deciding the field,
 * through a faster route than retyping the value by hand.
 */
export function useResolveConflict(
  permission: MemoryActionPermission = PERMISSIONS.MC_DECLARATIONS_REVIEW,
) {
  const mutation = useMutation({
    mutationFn: (target: MemoryFeedbackTarget) =>
      postFeedback("resolve-conflict", target),
  })
  return useGuardedMutation(mutation, permission)
}

/**
 * An entry's whole history — every document that stated, confirmed or corrected it, every
 * rating it was given, every supersession, conflict or retirement. NOT the one live
 * suggestion a surface shows; that is what `for_surface()` already answers, and this is
 * deliberately the only other way into memory's tables.
 *
 * LAZY BY CONVENTION — pass `enabled: false` until the panel that shows this is actually
 * opened. The history is paginated specifically because it can be long, and fetching it
 * eagerly on every render of a suggestion box would mean up to one request per entry on a
 * screen almost nobody expands further than `because`.
 *
 * `surface` IS REQUIRED, mirroring the endpoint: a caller must prove which screen's entry
 * this is, the same requirement accept/revert already carry.
 */
export function useMemoryLineage(
  entryId: string | null,
  surface: string,
  params: { limit?: number } = {},
  enabled = true,
) {
  return useInfiniteQuery(
    mcCursorList<MemoryLineageEvent>(
      ["mc", "memory", entryId, "lineage", surface, params],
      `/material-compliance/memory/${entryId}/lineage`,
      { surface, ...params },
      enabled && !!entryId,
    ),
  )
}

/**
 * Record that these hints reached a screen.
 *
 * THE COUNTER-METRIC, and the reason it is a call rather than a side effect of the read:
 * the headline goal is fewer repeat corrections, and that number also falls when people
 * simply stop bothering to correct anything. It is only meaningful beside "shown and
 * ignored", which needs a row for a suggestion that was seen and not acted on — and only
 * the client knows what was actually rendered. A hint fetched for a collapsed row was
 * never shown to anyone.
 */
export function useRecordMemoryShown(
  permission: MemoryActionPermission = PERMISSIONS.MC_DECLARATIONS_REVIEW,
) {
  const mutation = useMutation({
    mutationFn: async (shown: MemoryFeedbackTarget[]): Promise<void> => {
      if (shown.length === 0) return
      await apiClientV2.post("/material-compliance/memory/shown", {
        shown: shown.map((s) => ({
          entryId: s.entryId,
          surface: s.surface,
          decision: "suggest",
          targetType: s.targetType,
          targetId: s.targetId,
        })),
      })
    },
  })
  return useGuardedMutation(mutation, permission)
}
